<script setup>
import { ref, computed, onMounted } from "vue";
import { InputPrimary, ButtonAccentPrimary } from "@globus/ui-kit";

const fields = [
  { key: "item", label: "item", placeholder: "//div[@class=\"news-item\"]" },
  { key: "title", label: "title", placeholder: ".//h2/a/text()" },
  { key: "content", label: "content", placeholder: ".//p/text()" },
  { key: "uri", label: "uri", placeholder: ".//a/@href" },
  { key: "timestamp", label: "timestamp", placeholder: ".//span[@class=\"date\"]/text()" }
];

const DEFAULT_API_BASE = "https://ml-news-xpath.tz-world.ru/api/v1/rss/get_news_feed";

const defaultApiBase = ref(DEFAULT_API_BASE);
const apiBase = ref(DEFAULT_API_BASE);
const pageUrl = ref("");
const params = ref({
  item: "",
  title: "",
  content: "",
  uri: "",
  timestamp: ""
});
const copied = ref(false);

const encode = (str) => {
  if (!str) return "";

  return encodeURIComponent(str)
      .replace(/\(/g, "%28")
      .replace(/\)/g, "%29")
      .replace(/\[/g, "%5B")
      .replace(/\]/g, "%5D")
      .replace(/'/g, "%27")
      .replace(/ /g, "%20")
      .replace(/:/g, "%3A")
      .replace(/@/g, "%40");
};

const fullLink = computed(() => {
  if (!apiBase.value || !pageUrl.value) return "";

  const queryParams = [`url=${encode(pageUrl.value)}`];

  fields.forEach(({ key }) => {
    const value = params.value[key];
    if (value) queryParams.push(`${key}=${encode(value)}`);
  });

  return `${apiBase.value}?${queryParams.join("&")}`;
});

const reset = () => {
  pageUrl.value = "";
  params.value = {
    item: "",
    title: "",
    content: "",
    uri: "",
    timestamp: ""
  };
};

const copy = async () => {
  if (!fullLink.value) return;

  try {
    await navigator.clipboard.writeText(fullLink.value);
    copied.value = true;
    setTimeout(() => {
      copied.value = false;
    }, 2000);
  } catch (error) {
    console.error("Failed to copy:", error);
  }
};

onMounted(() => {
  const envValue = process.env.VUE_APP_PARSER_URL;

  if (envValue) {
    defaultApiBase.value = envValue;
    apiBase.value = envValue;
  }
});
</script>

<template>
  <div class="rss-builder">
    <div class="rss-builder__card">
      <div class="rss-builder__grid">
        <InputPrimary
          v-model="apiBase"
          size="large"
          label="API URL"
          :placeholder="defaultApiBase"
          class="rss-builder__field"
        />

        <InputPrimary
          v-model="pageUrl"
          size="large"
          label="URL страницы"
          placeholder="https://example.com/news"
          class="rss-builder__field"
        />
      </div>

      <div class="rss-builder__grid">
        <InputPrimary
          v-for="field in fields"
          :key="field.key"
          v-model="params[field.key]"
          size="large"
          :label="field.label"
          :placeholder="field.placeholder"
          class="rss-builder__field rss-builder__field--mono"
        />

        <div class="rss-builder__action">
          <ButtonAccentPrimary
            size="large"
            class="rss-builder__button"
            @click="reset"
          >
            Сбросить все
          </ButtonAccentPrimary>
        </div>
      </div>

      <div class="rss-builder__result">
        <label class="rss-builder__result-label">Ссылка</label>
        <div class="rss-builder__result-wrapper">
          <code class="rss-builder__result-code">
            {{ fullLink || 'Заполните поля для генерации ссылки' }}
          </code>
          <ButtonAccentPrimary
            v-if="fullLink"
            :class="{ 'rss-builder__copy-btn--copied': copied }"
            class="rss-builder__copy-btn"
            size="large"
            @click="copy"
          >
            {{ copied ? '✓ Скопировано' : '📋 Копировать' }}
          </ButtonAccentPrimary>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.rss-builder {
  background: #f0f2f5;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem 2rem;

  &__card {
    max-width: 1000px;
    width: 100%;
    background: #fff;
    border-radius: 24px;
    box-shadow: 0 20px 35px -12px rgba(0, 0, 0, 0.1);
    padding: 2rem;
  }

  &__field {
    margin-bottom: 1.25rem;

    &--mono {
      :deep(input) {
        font-family: 'SF Mono', 'Monaco', 'Menlo', monospace;
        font-size: 0.875rem;
      }
    }
  }

  &__grid {
    display: grid;
    gap: 1rem;
    margin: 1.5rem 0;

    @media (min-width: 768px) {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  &__action {
    display: flex;
    align-items: flex-end;
    margin-bottom: 1.25rem;
  }

  &__button {
    width: 100%;
  }

  &__result {
    margin-top: 1.5rem;
    padding-top: 1.5rem;
    border-top: 1px solid #e9ecef;

    &-label {
      display: block;
      margin-bottom: 0.75rem;
      font-size: 0.875rem;
      font-weight: 600;
      color: #2c3e50;
      letter-spacing: 0.3px;
    }

    &-wrapper {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;

      @media (min-width: 768px) {
        flex-direction: row;
      }
    }

    &-code {
      flex: 1;
      padding: 1rem;
      background: #f8f9fa;
      border: 1px solid #e9ecef;
      border-radius: 12px;
      font-family: 'SF Mono', 'Monaco', monospace;
      font-size: 0.75rem;
      line-height: 1.5;
      word-break: break-all;
      white-space: pre-wrap;
      max-height: 180px;
      overflow-y: auto;
      color: #1a2a3a;
    }
  }

  &__copy-btn {
    width: 100%;

    @media (min-width: 768px) {
      width: auto;
      min-width: 130px;
    }

    &--copied {
      background-color: #10b981;
      border-color: #10b981;
    }
  }
}
</style>
